import { useEffect, useRef } from 'react';

const vertexShader = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;

const fragmentShader = `
precision highp float;
varying vec2 v_uv;
uniform vec2 u_resolution;
uniform vec2 u_pointer;
uniform float u_time;

float field(vec2 p, float t) {
  float a = sin(p.x * 3.1 + t * 0.42);
  float b = sin((p.y + a * 0.18) * 4.2 - t * 0.31);
  float c = sin((p.x + p.y) * 2.4 + t * 0.22);
  return (a + b + c) / 3.0;
}

void main() {
  vec2 uv = v_uv;
  vec2 p = (uv - 0.5) * vec2(u_resolution.x / max(u_resolution.y, 1.0), 1.0);
  vec2 pointer = (u_pointer - 0.5) * vec2(u_resolution.x / max(u_resolution.y, 1.0), 1.0);
  float f = field(p * 1.35, u_time);
  float ridge = pow(1.0 - abs(f), 12.0);
  float pointerLight = exp(-4.5 * length(p - pointer));
  vec3 base = vec3(0.025, 0.026, 0.035);
  vec3 cobalt = vec3(0.16, 0.22, 0.88);
  vec3 violet = vec3(0.57, 0.20, 0.88);
  vec3 chrome = vec3(0.78, 0.81, 0.92);
  float energy = smoothstep(-0.35, 0.7, f) * 0.22 + ridge * 0.42;
  vec3 color = base;
  color += mix(cobalt, violet, uv.x * 0.72 + uv.y * 0.28) * energy;
  color += chrome * ridge * (0.12 + pointerLight * 0.28);
  color += cobalt * pointerLight * 0.08;
  float vignette = smoothstep(0.92, 0.28, length(uv - 0.5));
  color *= 0.46 + vignette * 0.76;
  gl_FragColor = vec4(color, 1.0);
}`;

function makeShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('WebGL shader unavailable');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(shader) ?? 'WebGL shader compilation failed');
  }
  return shader;
}

export function AlloyBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl', { alpha: false, antialias: false });
    if (!gl) {
      canvas.dataset.fallback = 'true';
      return;
    }
    const surface = canvas;
    const context = gl;

    const program = gl.createProgram();
    if (!program) return;
    try {
      gl.attachShader(program, makeShader(gl, gl.VERTEX_SHADER, vertexShader));
      gl.attachShader(program, makeShader(gl, gl.FRAGMENT_SHADER, fragmentShader));
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('WebGL link failed');
    } catch {
      canvas.dataset.fallback = 'true';
      return;
    }

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    gl.useProgram(program);
    const position = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const resolution = gl.getUniformLocation(program, 'u_resolution');
    const pointerLocation = gl.getUniformLocation(program, 'u_pointer');
    const timeLocation = gl.getUniformLocation(program, 'u_time');
    const pointer = { x: 0.68, y: 0.34 };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let animationFrame = 0;
    let lastFrame = 0;

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const width = Math.round(surface.clientWidth * dpr);
      const height = Math.round(surface.clientHeight * dpr);
      if (surface.width !== width || surface.height !== height) {
        surface.width = width;
        surface.height = height;
        context.viewport(0, 0, width, height);
      }
    }

    function render(timestamp: number) {
      if (timestamp - lastFrame >= 30 || reducedMotion) {
        resize();
        context.uniform2f(resolution, surface.width, surface.height);
        context.uniform2f(pointerLocation, pointer.x, pointer.y);
        context.uniform1f(timeLocation, reducedMotion ? 0.8 : timestamp / 1000);
        context.drawArrays(context.TRIANGLES, 0, 6);
        lastFrame = timestamp;
      }
      if (!reducedMotion && !document.hidden) animationFrame = requestAnimationFrame(render);
    }

    function handlePointer(event: PointerEvent) {
      pointer.x = event.clientX / window.innerWidth;
      pointer.y = 1 - event.clientY / window.innerHeight;
    }

    function handleVisibility() {
      cancelAnimationFrame(animationFrame);
      if (!document.hidden && !reducedMotion) animationFrame = requestAnimationFrame(render);
    }

    window.addEventListener('pointermove', handlePointer, { passive: true });
    document.addEventListener('visibilitychange', handleVisibility);
    animationFrame = requestAnimationFrame(render);
    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('pointermove', handlePointer);
      document.removeEventListener('visibilitychange', handleVisibility);
      gl.deleteProgram(program);
      gl.deleteBuffer(buffer);
    };
  }, []);

  return <canvas ref={canvasRef} className="alloy-background" aria-hidden="true" />;
}
